import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/Api';
import AuthContext from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const CourseWorkSubjectLevel = ({ classId }) => {
    const [courseworks, setCourseworks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { employeeNumber } = useContext(AuthContext);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const navigate = useNavigate();

    const [newCoursework, setNewCoursework] = useState({
        Description: '',
        DueDate: '',
        TotalMarks: ''
    });

    const fetchCourseworks = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}/api/courseworks/class-summary/${classId}`, {
                params: { page: currentPage }
            });
            setCourseworks(response.data.courseworks);
            setTotalPages(response.data.pagination.totalPages);
            setError(null);
        } catch (err) {
            setError('Failed to fetch courseworks');
            console.error('Error fetching courseworks:', err);
        } finally {
            setIsLoading(false);
        }
    }, [classId, currentPage]); // Dependencies

    useEffect(() => {
        fetchCourseworks();
    }, [fetchCourseworks]); // No ESLint warning now

    const handleCreateCoursework = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await axios.post(`${BASE_URL}/api/courseworks/create`, {
                TeacherID: employeeNumber,
                ClassID: classId,
                ...newCoursework
            });

            setIsModalOpen(false);
            setNewCoursework({
                Description: '',
                DueDate: '',
                TotalMarks: ''
            });
            fetchCourseworks(); // Refresh the list
        } catch (err) {
            setError('Failed to create coursework');
            console.error('Error creating coursework:', err);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold text-gray-800">Class Coursework</h2>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-200 flex items-center space-x-2"
                >
                    <span>Add New Coursework</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>

            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-r">
                    <p className="font-bold">Error</p>
                    <p>{error}</p>
                </div>
            )}

            <div className="grid gap-6">
                {courseworks.map((coursework) => (
                    <div
                        key={`${coursework.Description}-${coursework.DueDate}`}
                        className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
                    >
                        <div className="flex justify-between items-start">
                            <div className="space-y-3 flex-1">
                                <p className="text-lg font-medium text-gray-800">{coursework.Description}</p>
                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                    <div className="flex items-center">
                                        <span>Due: {new Date(coursework.DueDate).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <span>Marks: {coursework.TotalMarks}</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate(`/coursework-results/${coursework.id}`)}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200"
                            >
                                Results
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Create Coursework Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl p-8 w-full max-w-md">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">Create New Coursework</h3>
                        <form onSubmit={handleCreateCoursework} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={newCoursework.Description}
                                    onChange={(e) => setNewCoursework({ ...newCoursework, Description: e.target.value })}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300"
                                    rows="4"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                                    <input
                                        type="date"
                                        value={newCoursework.DueDate}
                                        onChange={(e) => setNewCoursework({ ...newCoursework, DueDate: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Total Marks</label>
                                    <input
                                        type="number"
                                        value={newCoursework.TotalMarks}
                                        onChange={(e) => setNewCoursework({ ...newCoursework, TotalMarks: e.target.value })}
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end space-x-4 mt-8">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2 border border-gray-300 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Creating...' : 'Create Coursework'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-center mt-6 space-x-2">
                    <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="px-4 py-2 border rounded">Previous</button>
                    <span className="px-4 py-2">Page {currentPage} of {totalPages}</span>
                    <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage >= totalPages} className="px-4 py-2 border rounded">Next</button>
                </div>
            )}
        </div>
    );
};

export default CourseWorkSubjectLevel;
